import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Flex } from '../../../components/ui/Flex';
import { ModalBodyContent, ModalHeader, StyledModalBody, StyledModalV2 } from '../../../components/Modals';
import { closeActiveModal } from '../../../helper/modals';
import { Separator } from '../../../components/ui/Separator';
import { requestFail, requestPending, requestSuccess } from '../../../utils/status';
import { GET_GRATA_INTERCOM_USER } from '../../../redux/constants';
import { Skeleton } from '../../../components/ui/Skeleton';
import styled from 'styled-components';
import { getGrataIntercomUser } from '../../../redux/actions/intercom';
import { InfoSection, IntercomGrid, UserInfo, UserInfoText } from '../IntercomComponents';
import { formatPhoneNumber } from '../../../helper/phoneNumber';

const ViewIntercomUserModal = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation();
  const { modal, data: intercomUser } = useSelector((state) => state.headerModal);
  const grataUserData = useSelector((state) => state.intercom.getGrataUserData);
  const grataUserStatus = useSelector((state) => state.intercom.getGrataUserStatus);

  const grataUserInformation = useMemo(() => {
    if (grataUserStatus === requestFail(GET_GRATA_INTERCOM_USER)) {
      return (
        <UserInfoText>{t('Failed to fetch user information')}</UserInfoText>
      )
    }

    if (grataUserStatus === requestPending(GET_GRATA_INTERCOM_USER)) {
      return (
        <Flex gap="1rem" direction="column">
          <UserStatusSkeleton />
          <UserStatusSkeleton />
          <UserStatusSkeleton />
        </Flex>
      )
    }

    if (grataUserStatus === requestSuccess(GET_GRATA_INTERCOM_USER) && grataUserData) {
      const { first_name, last_name, email, unit_num, home_phone, cell_phone } = grataUserData

      return (
        <Flex direction="column" gap="16px">
          <UserInfo title="Full Name" value={`${first_name} ${last_name}`} />
          <UserInfo title="Email" value={email} />
          <UserInfo title="Unit" value={unit_num} />
          <IntercomGrid>
            <UserInfo title="Home Phone Number" value={formatPhoneNumber(home_phone)} />
            <UserInfo title="Cell Phone Number" value={formatPhoneNumber(cell_phone)} />
          </IntercomGrid>
        </Flex>
      )
    }
  }, [grataUserStatus])

  useEffect(() => {
    dispatch(
      getGrataIntercomUser({
        userId: intercomUser.user_id
      })
    );
  }, [intercomUser.user_id])

  return (
    <StyledModalV2
      show={modal === 'ViewIntercomUserModal'}
      onClose={closeActiveModal}
      closeOnBackdrop={false}
      width={'550px'}
      side
    >
      <StyledModalBody>
        <ModalHeader v2 title={`${t('View intercom user information')}`} />
        <ModalBodyContent>
          <Flex direction="column" gap="16px">
            <InfoSection
              title={t('Intercom information')}
              subtitle={t('This is the information directly from the intercom')}
            >
              <UserInfo title={t("Full Name")} value={`${intercomUser.display_name}`} />
              <IntercomGrid>
                <UserInfo title="Phone Number" value={formatPhoneNumber(intercomUser.phone_number)} />
                {intercomUser?.private_pin && (
                  <UserInfo title="Private pin" value={intercomUser.private_pin} />
                )}
              </IntercomGrid>
            </InfoSection>

            <Separator orientation="horizontal" />

            <InfoSection
              title={t('Grata Information')}
              subtitle={t('This is the information from Grata')}
            >
              {grataUserInformation}
            </InfoSection>

          </Flex>
        </ModalBodyContent>
      </StyledModalBody>
    </StyledModalV2 >
  );
};


const UserStatusSkeleton = styled(Skeleton)`
  border-radius: 12px;
  height: 24px;
  width: 100%;
`

export default ViewIntercomUserModal;
